// FlashcardsView — SRS Leitner flashcard drill.
//
// Decision (Session 19G §5.5):
//   - On mount: build a queue from cards due now (or all when ?all=1).
//     Cards rated 1 (Again) get re-pushed to queue tail this session.
//   - State: queue, index, isFlipped. Pure, no globals.
//   - Keyboard: Space/Enter flips on front; on back, Space/Enter is "Good" (rating 3).
//     Number keys 1-4 rate when flipped.
//   - On rating: persist via updateProgress(courseId, ...), then advance index.
//   - When index ≥ queue.length: session-done screen (count of "Good or above").

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MarkdownBlock } from '../components/markdown-block.js';
import { EditableField } from '../components/editable-field.js';
import { ItemActions } from '../components/item-actions.js';
import { StateCard } from '../components/state-card.js';
import { Bookmark, Layers, PartyPopper } from 'lucide-react';
import type { Flashcard } from '@examforge/shared/schemas';
import { useT, useLocale } from '../i18n/index.js';
import {
  BOX_LABELS,
  entryFor,
  getDueCards,
  rateCard,
  type SrsRating,
} from '../lib/srs.js';
import { updateProgress } from '../storage/idb.js';
import { isTypingTarget } from '../lib/keyboard.js';
import { shuffle } from '../lib/quiz-utils.js';
import { toggleBookmark } from '../lib/bookmark.js';
import { filterByTopic } from '../lib/item-filter.js';
// Isomorphism Phase 3: Zustand + Event Bus (replacing Context-only).
import { useStoreCourseData } from '../lib/hooks/use-store-course.js';
import { useEventBus } from '../lib/hooks/use-event-bus.js';

export function FlashcardsView() {
  const t = useT();
  const { locale } = useLocale();
  // Isomorphism Phase 3: Zustand primary + Event Bus for cross-component updates.
  const { draft, pkg, progress, refreshProgress } = useStoreCourseData();
  const eventBus = useEventBus();
  const [params, setParams] = useSearchParams();
  const reviewAll = params.get('all') === '1';
  const filterCase = params.get('case');
  // 19AW: ?topic= deep-link from dashboard topic cards / weakest-topic list.
  // Topic filter is applied AFTER the case filter (composes; in practice
  // dashboard sends one or the other, not both).
  const filterTopic = params.get('topic');
  const navigate = useNavigate();

  const initialQueue = useMemo(() => {
    let pool = pkg.flashcards;
    if (filterCase) pool = pool.filter((fc) => fc.caseId === filterCase);
    if (filterTopic) pool = filterByTopic(pool, filterTopic);
    if (reviewAll) return shuffle(pool);
    return shuffle(getDueCards(pool, progress, { caseId: filterCase ?? null }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg.flashcards, reviewAll, filterCase, filterTopic]); // intentionally exclude `progress`

  const [queue, setQueue] = useState<Flashcard[]>(initialQueue);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [goodCount, setGoodCount] = useState(0);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset queue/index/flipped/goodCount when the shuffled initialQueue recomputes (deck/topic filter change); key-reset alternative would remount the view
    setQueue(initialQueue);
    setIndex(0);
    setFlipped(false);
    setGoodCount(0);
  }, [initialQueue]);

  const current = queue[index];

  async function rate(rating: SrsRating): Promise<void> {
    if (!current) return;
    const prev = entryFor(progress, current.id);
    const next = rateCard(prev, rating);
    await updateProgress(draft.id, (p) => {
      p.srs[current.id] = next;
    });
    if (rating >= 3) setGoodCount((c) => c + 1);
    if (rating === 1) {
      // Push current back to the queue tail for re-review this session.
      setQueue((q) => [...q, current]);
    }
    await refreshProgress();
    // Isomorphism Phase 3: notify Event Bus so Dashboard can live-refresh.
    eventBus.emit('flashcard:reviewed', {
      courseId: draft.id,
      itemId: current.id,
      rating,
    });
    setIndex((i) => i + 1);
    setFlipped(false);
  }

  async function handleToggleBookmark(): Promise<void> {
    if (!current) return;
    await toggleBookmark(
      draft.id,
      current.id,
      'flashcard',
      current.caseId ?? null,
      updateProgress,
      refreshProgress,
    );
  }

  // Keyboard shortcuts.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isTypingTarget(e)) return;
      if (!current) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        if (flipped) void rate(3);
        else setFlipped(true);
        return;
      }
      if (e.key.toLowerCase() === 'b') {
        e.preventDefault();
        void handleToggleBookmark();
        return;
      }
      if (flipped) {
        if (e.key === '1') { e.preventDefault(); void rate(1); }
        else if (e.key === '2') { e.preventDefault(); void rate(2); }
        else if (e.key === '3') { e.preventDefault(); void rate(3); }
        else if (e.key === '4') { e.preventDefault(); void rate(4); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // 19BM-C: re-bind listener only when the active card or flip state changes.
    // `rate` is a stable hook callback; including it would needlessly rebuild
    // the listener. current/flipped are the only values that gate which key
    // branch runs.
  }, [current, flipped]); // eslint-disable-line react-hooks/exhaustive-deps

  // Empty deck — generated course has no flashcards at all.
  if (pkg.flashcards.length === 0) {
    return (
      <StateCard
        variant="empty"
        icon={<Layers size={48} />}
        title={t('flashcards.empty')}
        hint={t('flashcards.emptyHint')}
      />
    );
  }

  const cases = pkg.cases;
  const filterDropdown = (
    <select
      className="field-input field-input-narrow"
      value={filterCase ?? ''}
      onChange={(e) => {
        const next = new URLSearchParams(params);
        if (e.target.value) next.set('case', e.target.value);
        else next.delete('case');
        setParams(next);
      }}
    >
      <option value="">{t('flashcards.allCases')}</option>
      {cases.map((c) => (
        <option key={c.id} value={c.id}>{c.title}</option>
      ))}
    </select>
  );

  // Session done — queue exhausted.
  if (!current) {
    return (
      <div className="flashcards-session-done">
        <PartyPopper size={48} aria-hidden="true" className="session-done-icon" />
        <h2>{t('flashcards.sessionDone')}</h2>
        <p>{t('flashcards.sessionDoneHint')}</p>
        <p className="flashcards-session-stat">
          {t('flashcards.sessionCorrect', { good: goodCount, total: queue.length })}
        </p>
        <div className="flashcards-session-buttons">
          {!reviewAll && (
            <button
              className="btn-secondary"
              onClick={() => {
                const next = new URLSearchParams(params);
                next.set('all', '1');
                setParams(next);
              }}
            >
              {t('flashcards.reviewAll')}
            </button>
          )}
          <button className="btn-primary" onClick={() => navigate('..')}>
            {t('quiz.finishedBackToHub')}
          </button>
        </div>
      </div>
    );
  }

  const srsEntry = entryFor(progress, current.id);
  const isBookmarked = Boolean(progress.bookmarks[current.id]);

  return (
    <div className="flashcards-view">
      <div className="flashcards-toolbar">
        <span className="flashcards-counter">
          {t('flashcards.cardCounter', { current: index + 1, total: queue.length })}
        </span>
        <span className="flashcards-box">
          {t('flashcards.boxLabel')}: {BOX_LABELS[srsEntry.box]?.[locale] ?? srsEntry.box}
        </span>
        <button
          type="button"
          className={`bookmark-btn${isBookmarked ? ' is-active' : ''}`}
          onClick={(e) => { e.stopPropagation(); void handleToggleBookmark(); }}
          title={isBookmarked ? t('bookmarks.removeBookmark') : t('bookmarks.addBookmark')}
        >
          <Bookmark size={14} fill={isBookmarked ? 'currentColor' : 'none'} aria-hidden="true" />
        </button>
        <label className="flashcards-filter">
          <span>{t('flashcards.filterByCase')}</span>
          {filterDropdown}
        </label>
        <ItemActions
          courseId={draft.id}
          item={{
            id: current.id,
            caseId: current.caseId ?? undefined,
            conceptIds: (current as Record<string, unknown>)['conceptIds'] as string[] | undefined,
          }}
          stage="flashcards"
          onRegenComplete={() => { void refreshProgress(); }}
        />
      </div>

      <div className="flashcard-frame">
        <div
          className={`flashcard-inner${flipped ? ' is-flipped' : ''}`}
          onClick={() => { if (!flipped) setFlipped(true); }}
          role="button"
          tabIndex={0}
          aria-pressed={flipped}
        >
          {/* Both faces always rendered; backface-visibility hides the away one.
              grid-area: 'face' stacks them so .flashcard-inner height = max(front, back). */}
          <article className="flashcard-face flashcard-front-face" aria-hidden={flipped}>
            <div className="flashcard-content markdown-body">
              <MarkdownBlock>{current.front}</MarkdownBlock>
            </div>
            <p className="flashcard-flip-hint">{t('flashcards.flipHint')}</p>
          </article>
          <article className="flashcard-face flashcard-back-face" aria-hidden={!flipped}>
            <div className="flashcard-content markdown-body">
              {/* 19AQ · F4: back text is the most-edited field on flashcards
                  (主母 corrects AI explanations).  EditableField wraps it. */}
              <EditableField
                itemId={current.id}
                itemKind="flashcard"
                field="back"
                aiOriginal={current.back}
              />
            </div>
          </article>
        </div>
      </div>

      {flipped && (
        <div className="flashcards-rate-row">
          <button className="rate-btn rate-1" onClick={() => rate(1)}>
            <span className="rate-key">1</span> {t('flashcards.rateAgain')}
          </button>
          <button className="rate-btn rate-2" onClick={() => rate(2)}>
            <span className="rate-key">2</span> {t('flashcards.rateHard')}
          </button>
          <button className="rate-btn rate-3" onClick={() => rate(3)}>
            <span className="rate-key">3</span> {t('flashcards.rateGood')}
          </button>
          <button className="rate-btn rate-4" onClick={() => rate(4)}>
            <span className="rate-key">4</span> {t('flashcards.rateEasy')}
          </button>
        </div>
      )}

      <p className="flashcards-shortcuts">{t('flashcards.shortcuts')}</p>
    </div>
  );
}

